import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/context/LanguageContext';
import { errMsg } from '@/lib/api';

/**
 * Handles the `#session_id=...` fragment returned by Emergent Google Auth.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const { t } = useI18n();
  const hasProcessed = useRef(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const sessionId = new URLSearchParams(hash.replace(/^#/, '')).get('session_id');

    if (!sessionId) {
      navigate('/giris', { replace: true });
      return;
    }

    (async () => {
      try {
        await loginWithGoogleSession(sessionId);
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/studyo', { replace: true });
      } catch (e) {
        window.history.replaceState(null, '', window.location.pathname);
        setError(errMsg(e, t('auth.googleFailed')));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="grid min-h-screen place-items-center noise-bg px-4 py-10">
      <div className="w-full max-w-[420px] text-center">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>
        {error ? (
          <div data-testid="google-auth-error" className="panel p-6 sm:p-8">
            <h1 className="font-display text-xl font-semibold">{t('auth.googleFailed')}</h1>
            <p className="mt-2 text-sm text-[hsl(var(--text-muted))]">{error}</p>
            <Button
              onClick={() => navigate('/giris', { replace: true })}
              data-testid="google-auth-error-back"
              className="mt-6 h-11 w-full bg-[hsl(var(--brand-red))] font-semibold text-white hover:bg-[hsl(var(--brand-red-2))]"
            >
              {t('auth.loginTitle')}
            </Button>
          </div>
        ) : (
          <div data-testid="google-auth-loading" className="panel flex flex-col items-center gap-3 p-8">
            <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--brand-red))]" />
            <p className="text-sm text-[hsl(var(--text-muted))]">{t('auth.googleSigningIn')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
